/**
 * Lazy-load real PPTX speaker notes when the slide document has none.
 *
 * Decks ingested before #2336 may have empty ``speaker_notes`` in Cosmos
 * even though the stored single-slide PPTX still carries the notes page.
 * Presenter View calls the on-demand backfill endpoint only when needed.
 */

import { useEffect, useState } from "react"

import { fetchSpeakerNotes } from "../../api"

// Session-scoped cache of backfilled notes, keyed by slide id. Revisiting a
// slide (common when clicking back and forth in Presenter View) then reuses
// the already-fetched notes instead of refetching and briefly flashing an
// empty pane while Cosmos still reports empty speaker_notes on the synced
// slide. Only NON-EMPTY results are cached: an empty result is left uncached
// so a revisit refetches and can pick up notes that appeared server-side
// (reprocess / backfill elsewhere) — re-requesting a genuinely note-less
// slide just returns empty again with no visible flash. Transient failures
// are likewise uncached so they retry. Entries are small strings bounded by
// the number of slides viewed this session.
const notesCache = new Map<string, string>()

export function useSpeakerNotesBackfill(slideId: string | undefined, storedNotes: string): string {
  const [backfill, setBackfill] = useState(() => (slideId ? (notesCache.get(slideId) ?? "") : ""))

  useEffect(() => {
    // Seed synchronously from the cache (only ever holds non-empty notes) so
    // a revisit of a slide with notes doesn't re-flash empty.
    const cached = slideId ? notesCache.get(slideId) : undefined
    setBackfill(cached ?? "")

    if (!slideId || storedNotes.trim().length > 0) {
      return
    }
    // Real notes already cached for this slide — no refetch. An empty/absent
    // cache entry falls through so newly-available notes can be picked up.
    if (cached) {
      return
    }

    let cancelled = false
    void fetchSpeakerNotes(slideId)
      .then((resp) => {
        const notes = resp.speaker_notes.trim()
        // Cache only real notes: empties stay uncached so a later revisit
        // re-checks in case the slide gained notes server-side.
        if (notes) {
          notesCache.set(slideId, notes)
        }
        if (!cancelled) {
          setBackfill(notes)
        }
      })
      .catch(() => {
        // Leave uncached so a later revisit retries the (possibly transient)
        // failure rather than sticking with an empty pane forever.
        if (!cancelled) {
          setBackfill("")
        }
      })

    return () => {
      cancelled = true
    }
  }, [slideId, storedNotes])

  return storedNotes.trim().length > 0 ? storedNotes.trim() : backfill
}
